import { Alert, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { FormEventHandler, useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '../../shared/components/Dialogs/FormDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type {
  LLMModelSettings,
  Project,
  TenantLLMEffortLevel,
  TenantLLMProviderType,
} from '../../core/types/data-models';

const PROVIDERS: TenantLLMProviderType[] = [
  'claude',
  'gemini',
  'openai',
  'claude-code-cli',
  'claude-sdk-agent',
  'openai-agent',
  'claude-cli-agent',
  'perplexity',
];
const EFFORTS: TenantLLMEffortLevel[] = ['low', 'medium', 'high', 'xhigh', 'max'];

const TIERS = [
  { key: 'llmFastModelSettings', label: 'Fast', description: 'Cheap/low-latency prompts.' },
  { key: 'llmHighModelSettings', label: 'High', description: 'Everyday quality prompts.' },
  { key: 'llmDeepModelSettings', label: 'Deep', description: 'Highest-quality, expensive prompts.' },
] as const;

/** All-string form shape so number/select fields can be empty ("use tier default"). */
interface TierFormState {
  provider: TenantLLMProviderType | '';
  model: string;
  maxTokens: string;
  temperature: string;
  effort: TenantLLMEffortLevel | '';
  maxWebSources: string;
  maxWebSearchQueries: string;
}

const EMPTY_TIER: TierFormState = {
  provider: '',
  model: '',
  maxTokens: '',
  temperature: '',
  effort: '',
  maxWebSources: '',
  maxWebSearchQueries: '',
};

function toFormState(settings: Partial<LLMModelSettings> | null): TierFormState {
  return {
    provider: settings?.provider ?? '',
    model: settings?.model ?? '',
    maxTokens: settings?.maxTokens != null ? String(settings.maxTokens) : '',
    temperature: settings?.temperature != null ? String(settings.temperature) : '',
    effort: settings?.effort ?? '',
    maxWebSources: settings?.maxWebSources != null ? String(settings.maxWebSources) : '',
    maxWebSearchQueries: settings?.maxWebSearchQueries != null ? String(settings.maxWebSearchQueries) : '',
  };
}

/** Only the fields the admin actually set — `null` when every field was left blank. */
function toPayload(form: TierFormState): Partial<LLMModelSettings> | null {
  const payload: Partial<LLMModelSettings> = {};
  if (form.provider !== '') payload.provider = form.provider;
  if (form.model.trim() !== '') payload.model = form.model.trim();
  if (form.maxTokens !== '') payload.maxTokens = Number(form.maxTokens);
  if (form.temperature !== '') payload.temperature = Number(form.temperature);
  if (form.effort !== '') payload.effort = form.effort;
  if (form.maxWebSources !== '') payload.maxWebSources = Number(form.maxWebSources);
  if (form.maxWebSearchQueries !== '') payload.maxWebSearchQueries = Number(form.maxWebSearchQueries);
  return Object.keys(payload).length > 0 ? payload : null;
}

interface ProjectLlmSettingsDialogProps {
  open: boolean;
  project: Project | null;
  onClose: () => void;
}

export function ProjectLlmSettingsDialog({ open, project, onClose }: ProjectLlmSettingsDialogProps) {
  const qc = useQueryClient();
  const [forms, setForms] = useState<Record<(typeof TIERS)[number]['key'], TierFormState>>({
    llmFastModelSettings: EMPTY_TIER,
    llmHighModelSettings: EMPTY_TIER,
    llmDeepModelSettings: EMPTY_TIER,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && project) {
      setForms({
        llmFastModelSettings: toFormState(project.llmFastModelSettings),
        llmHighModelSettings: toFormState(project.llmHighModelSettings),
        llmDeepModelSettings: toFormState(project.llmDeepModelSettings),
      });
      setError(null);
    }
  }, [open, project]);

  const save = useMutation({
    mutationFn: async () => {
      if (!project) return;
      return resources.projects.update(project.id, {
        llmFastModelSettings: toPayload(forms.llmFastModelSettings),
        llmHighModelSettings: toPayload(forms.llmHighModelSettings),
        llmDeepModelSettings: toPayload(forms.llmDeepModelSettings),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const setField = (tierKey: (typeof TIERS)[number]['key'], field: keyof TierFormState, value: string) => {
    setForms((prev) => ({ ...prev, [tierKey]: { ...prev[tierKey], [field]: value } }));
  };

  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const missingProvider = TIERS.some(({ key }) => forms[key].provider === '');
    if (missingProvider) {
      setError('Provider is required for every tier — unlike the other fields, it has no tier default.');
      return;
    }
    setError(null);
    save.mutate();
  };

  return (
    <FormDialog
      open={open}
      title={`LLM Model Settings — ${project?.name ?? ''}`}
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={save.isPending}
      maxWidth="md"
      submitLabel="Save"
    >
      {error && <Alert severity="error">{error}</Alert>}
      <Typography variant="body2" color="text.secondary">
        Provider must be set explicitly for every tier. Leave any other field blank to fall back to the
        environment-configured default for that tier.
      </Typography>
      {TIERS.map(({ key, label, description }, i) => {
        const form = forms[key];
        return (
          <Stack key={key} spacing={1.5} sx={{ pt: i > 0 ? 1 : 0 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {label} tier
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {description}
              </Typography>
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Provider"
                value={form.provider}
                onChange={(e) => setField(key, 'provider', e.target.value)}
                sx={{ flex: 1 }}
              >
                {PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
              <TextField
                label="Model"
                placeholder="e.g. claude-sonnet-4-5"
                value={form.model}
                onChange={(e) => setField(key, 'model', e.target.value)}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Max tokens"
                type="number"
                value={form.maxTokens}
                onChange={(e) => setField(key, 'maxTokens', e.target.value)}
                inputProps={{ min: 1 }}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Temperature"
                type="number"
                value={form.temperature}
                onChange={(e) => setField(key, 'temperature', e.target.value)}
                inputProps={{ min: 0, max: 2, step: 0.05 }}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                select
                label="Effort"
                value={form.effort}
                onChange={(e) => setField(key, 'effort', e.target.value)}
                sx={{ flex: 1 }}
              >
                <MenuItem value="">Tier default</MenuItem>
                {EFFORTS.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </TextField>
              <TextField
                label="Max web sources"
                type="number"
                value={form.maxWebSources}
                onChange={(e) => setField(key, 'maxWebSources', e.target.value)}
                inputProps={{ min: 0 }}
                sx={{ flex: 1 }}
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Max web search queries"
                type="number"
                helperText="Claude and Perplexity only — caps searches (research steps on Perplexity), not sources kept"
                value={form.maxWebSearchQueries}
                onChange={(e) => setField(key, 'maxWebSearchQueries', e.target.value)}
                inputProps={{ min: 0 }}
                sx={{ flex: 1 }}
              />
            </Stack>
          </Stack>
        );
      })}
    </FormDialog>
  );
}
